import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/products", label: "Products" },
  { to: "/technologies", label: "Technologies" },
  { to: "/oem", label: "OEM" },
  { to: "/odm", label: "ODM" },
  { to: "/industries", label: "Industries" },
  { to: "/company", label: "Company" },
  { to: "/news", label: "News" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="backdrop-blur-xl bg-white/75 border-b border-slate-900/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <img src="/brand/wordmark.png" alt="ACTIVE" className="h-4 w-auto" />
            <span className="hidden sm:block font-mono text-[10px] tracking-[0.3em] text-slate-400 uppercase mt-0.5">Stairlifts</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" data-testid="nav-desktop">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-200 ${
                  location.pathname.startsWith(l.to) ? "text-slate-950" : "text-slate-500 hover:text-slate-950"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact?type=distributor"
              data-testid="nav-cta-distributor"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-obsidian text-white text-[13px] font-medium px-5 py-2.5 hover:bg-signal transition-colors duration-300"
            >
              Become a Distributor
              <ArrowUpRight size={14} />
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-slate-900"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden backdrop-blur-xl bg-white/95 border-b border-slate-900/[0.06]"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {[...LINKS, { to: "/downloads", label: "Downloads" }, { to: "/contact", label: "Contact" }].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  className="font-display text-2xl font-medium text-slate-900 py-2.5 border-b border-slate-100 last:border-0"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact?type=distributor"
                data-testid="nav-mobile-cta"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-obsidian text-white text-sm font-medium px-6 py-3.5"
              >
                Become a Distributor <ArrowUpRight size={15} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
